import React from "react";
import { Color } from "config/colors";
import { FontFamily } from "config/font";
import { useTranslation } from "react-i18next";
import {
  DropDown,
  DetailsHeadCell,
  TipButtonStyled,
  WorkerContainer,
  DetailsContainer,
  DropdownContainer,
  TipButtonContainer,
  TipDetailsContainer,
  MissingTimereportSup,
} from "../tips.styled";
import { Text } from "components/Typography/text.styled";
import { formatNumber, parseWorkTime } from "utils/utils";
import { PayoutType, TipDetails, TipStatus, Worker } from "model/Tip";
import { TipState } from "web-apps/company/stores/tipStore/tipStore.reducer";

interface TipDetailsProps {
  tip: TipDetails;
  tipState: TipState;
  isExpanded: boolean;
  openDistributeModal: (tipId: number) => void;
  handlePayout: (tipId: number, payoutType: PayoutType) => void;
  getWorkerStatus: (workerId: number, date: string) => JSX.Element;
}

export const TipDetailsComponent: React.FC<TipDetailsProps> = (props) => {
  const { t } = useTranslation();

  const groupWorkersByWorkgroup = (workers: Worker[]) => {
    return workers.reduce(
      (acc, worker) => {
        const workGroupKey = worker.workGroupId ?? "no-group";

        if (!acc[workGroupKey]) {
          acc[workGroupKey] = {
            workGroupName: worker.workGroupName || t("CompanyTip.NotChosen"),
            workers: [],
          };
        }
        acc[workGroupKey].workers.push(worker);
        return acc;
      },
      {} as Record<string, { workGroupName: string; workers: Worker[] }>,
    );
  };

  const groupedWorkers = groupWorkersByWorkgroup(props.tip?.workers ?? []);

  const isClosed =
    props.tip.status === TipStatus.Closed ||
    props.tip.status === TipStatus.ClosedManually;

  const shouldShowDistribute = !isClosed;
  const shouldShowPayout = props.tip.status === TipStatus.Done;

  const handleDistributeTip = () => {
    props.openDistributeModal(props.tip.companyTipId);
  };

  const hasWorkersWithoutApprovedTimeReports =
    props.tip.workers.length > 0 &&
    props.tip.workers.some((worker) => worker.approvedTimeReportCount <= 0);

  const isDistributeDisabled = props.tip.amount === 0;

  return (
    <DropdownContainer isOpen={props.isExpanded}>
      {props.isExpanded && (
        <DropDown>
          {props.tip?.workers.length ? (
            <DetailsContainer>
              <DetailsHeadCell>
                <Text fontFamily={FontFamily.MontserratSemiBold}>
                  {t("CompanyTip.Workers")}
                </Text>
                <Text
                  fontFamily={FontFamily.MontserratSemiBold}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  {t("CompanyTip.Hours")}
                </Text>
                <Text
                  fontFamily={FontFamily.MontserratSemiBold}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  {t("CompanyTip.Tips")}
                </Text>
                <Text
                  fontFamily={FontFamily.MontserratSemiBold}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  {t("CompanyTip.Payment")}
                </Text>
                <Text
                  fontFamily={FontFamily.MontserratSemiBold}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  {t("CompanyTip.Workgroup")}
                </Text>
              </DetailsHeadCell>
              {Object.entries(groupedWorkers).map(([workGroupId, group]) => (
                <div key={workGroupId} style={{ marginBottom: "10px" }}>
                  {group.workers.map((worker, index) => (
                    <TipDetailsContainer key={index} style={{ padding: "0" }}>
                      <WorkerContainer
                        style={{
                          display: "flex",
                          justifyContent: "start",
                        }}
                      >
                        <Text>
                          {worker.firstName} {worker.lastName}
                          {worker.approvedTimeReportCount <= 0 && (
                            <MissingTimereportSup>*</MissingTimereportSup>
                          )}
                        </Text>
                      </WorkerContainer>
                      <WorkerContainer>
                        <Text>
                          {parseWorkTime(worker.workTime).hours}
                          {t("General.HourShortened")}{" "}
                          {parseWorkTime(worker.workTime).minutes}
                          {t("General.MinutesShortened")}
                        </Text>
                      </WorkerContainer>
                      <WorkerContainer>
                        <Text>
                          {formatNumber(worker.tipAmount)} {t("CompanyTip.Kr")}
                        </Text>
                      </WorkerContainer>
                      <WorkerContainer>
                        {props.getWorkerStatus(worker.workerId, props.tip.date)}
                      </WorkerContainer>
                      <WorkerContainer>
                        <Text>
                          {worker.workGroupId
                            ? `${worker.workGroupName} (${
                                props.tip.workGroups.find(
                                  (group) =>
                                    group.workGroupId === worker.workGroupId,
                                )?.percentage ?? 0
                              }%)`
                            : t("CompanyTip.NotChosen")}
                        </Text>
                      </WorkerContainer>
                    </TipDetailsContainer>
                  ))}
                </div>
              ))}{" "}
              {props.tip.amount === 0 && (
                <Text>{t("CompanyTip.EnterTipAmountToDistribute")}</Text>
              )}
              {hasWorkersWithoutApprovedTimeReports && (
                <>
                  <Text>
                    {t("CompanyTip.WorkersWithoutApprovedTimeReports")}
                  </Text>
                </>
              )}
              {(shouldShowDistribute || shouldShowPayout) && (
                <TipButtonContainer
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: "15px",
                    marginBottom: "15px",
                  }}
                >
                  {shouldShowDistribute && (
                    <TipButtonStyled
                      onClick={handleDistributeTip}
                      backgroundColor={Color.MidnightBlue}
                      disabled={isDistributeDisabled}
                      style={{
                        marginRight: "10px",
                      }}
                    >
                      {t("CompanyTip.Distribute")}
                    </TipButtonStyled>
                  )}

                  {shouldShowPayout && (
                    <>
                      <TipButtonStyled
                        style={{ marginRight: "10px" }}
                        onClick={() => {
                          props.handlePayout(
                            props.tip.companyTipId,
                            PayoutType.Manual,
                          );
                        }}
                        backgroundColor={Color.MidnightBlue}
                      >
                        {t("CompanyTip.MarkAsManuallyPaid")}
                      </TipButtonStyled>
                      <TipButtonStyled
                        onClick={() => {
                          props.handlePayout(
                            props.tip.companyTipId,
                            PayoutType.Automatic,
                          );
                        }}
                        backgroundColor={Color.MidnightBlue}
                      >
                        {t("CompanyTip.PayoutWithSwish")}
                      </TipButtonStyled>
                    </>
                  )}
                </TipButtonContainer>
              )}
            </DetailsContainer>
          ) : (
            <>
              <Text>{t("CompanyTip.NoWorkers")}</Text>{" "}
              <TipButtonContainer
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "15px",
                  marginBottom: "15px",
                }}
              >
                <TipButtonStyled
                  onClick={handleDistributeTip}
                  disabled={isDistributeDisabled}
                  backgroundColor={Color.MidnightBlue}
                >
                  {t("CompanyTip.Distribute")}
                </TipButtonStyled>
              </TipButtonContainer>
            </>
          )}
        </DropDown>
      )}
    </DropdownContainer>
  );
};
